/**
 * appointmentHistoryActor.tsx
 *
 * Who to credit an appointment-history row to.
 *
 * Lives outside UnifiedAppointmentModal.tsx because that file reaches the app entry
 * through its store imports and boots React on load, so nothing in it can be
 * exercised on its own.
 *
 * Rows written outside a staff session carry `auditUserID = 0` and name the actor in
 * `auditChangeReason` instead: the inbound reminder-reply handler
 * (html/html/appt_confirm_reply.php) writes 'Confirmed by patient' / 'Cancelled by
 * patient' / 'Checked in by patient', and the bulk importer writes 'zenoti import'.
 * A positive id that resolves to no user row is an account that has since been
 * deleted. All four used to render as "(UNKNOWN) ((UNKNOWN))".
 *
 * The change reason steers the label and is never displayed: 'zenoti import' and
 * 'record update' are developer vocabulary.
 */
import type { AppointmentHistoryEntry } from '@/services/appointments.service';

/** The fields the label is derived from. */
export type HistoryActorFields = Pick<
  AppointmentHistoryEntry,
  'auditUserId' | 'auditUserName' | 'auditUserUsername' | 'auditChangeReason'
>;

export type HistoryActor = {
  /** Who to name, already in display form. */
  actor: string;
  /** Username to show in parentheses, or null to show none. */
  username: string | null;
  /** False for an actor that reads as an adverb ("automatically"), which takes no "by". */
  withBy: boolean;
};

export function historyActorLabel(entry: HistoryActorFields): HistoryActor {
  const name = (entry.auditUserName ?? '').trim();
  const username = (entry.auditUserUsername ?? '').trim();

  if (name !== '') {
    return { actor: name, username: username !== '' ? username : null, withBy: true };
  }

  // An account with no name on it still identifies a person.
  if (username !== '') {
    return { actor: username, username: null, withBy: true };
  }

  if (/\bby patient\b/i.test(entry.auditChangeReason ?? '')) {
    return { actor: 'the patient', username: null, withBy: true };
  }

  if (Number(entry.auditUserId) === 0) {
    return { actor: 'automatically', username: null, withBy: false };
  }

  return { actor: 'a removed user', username: null, withBy: true };
}

/** The trailing "by <actor> (<username>)" of a history row, including its leading space. */
export function HistoryActorText({ entry }: { entry: HistoryActorFields }) {
  const { actor, username, withBy } = historyActorLabel(entry);
  return (
    <>
      {withBy ? ' by ' : ' '}
      {actor}
      {username !== null && (
        <>
          {' '}
          <i className="text-slate-500">({username})</i>
        </>
      )}
    </>
  );
}
